import { type ReactNode, useState } from 'react';

import { cn } from '@/lib/utils';

import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Input } from './input';
import Text from './text';

export type DangerZoneItemProps = {
  title: string;
  description?: string;
  buttonText?: string;
  disabled?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmationText?: string;
  onConfirm: () => void | Promise<void>;
  isLoading?: boolean;
};

function DangerZoneItem({
  title,
  description,
  buttonText = 'Delete',
  disabled = false,
  confirmTitle,
  confirmDescription,
  confirmationText,
  onConfirm,
  isLoading = false,
}: Readonly<DangerZoneItemProps>) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [typed, setTyped] = useState('');

  const requiresTyping = !!confirmationText;
  const canConfirm = !requiresTyping || typed === confirmationText;

  const handleOpen = () => {
    setTyped('');
    setShowConfirm(true);
  };

  const handleClose = () => {
    setTyped('');
    setShowConfirm(false);
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm();
    handleClose();
  };

  return (
    <>
      <div className="flex flex-row flex-wrap items-center justify-between gap-5 sm:gap-4">
        <div className="flex flex-col gap-2 sm:gap-1">
          <Text weight="semibold">{title}</Text>
          {description && (
            <Text size="sm" variant="muted">
              {description}
            </Text>
          )}
        </div>
        <Button
          variant="destructive"
          onClick={handleOpen}
          disabled={isLoading || disabled}
          className="self-end"
        >
          {buttonText}
        </Button>
      </div>

      <Dialog
        open={showConfirm}
        onOpenChange={(open) => !open && handleClose()}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{confirmTitle ?? title}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-5 sm:gap-4">
            {confirmDescription && <Text>{confirmDescription}</Text>}
            {requiresTyping && (
              <div className="flex flex-col gap-2 sm:gap-1">
                <Text size="sm" variant="muted">
                  Type <strong>{confirmationText}</strong> to confirm:
                </Text>
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={confirmationText}
                  autoComplete="off"
                />
              </div>
            )}
            <div className="flex flex-row justify-end gap-5 sm:gap-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirm}
                disabled={!canConfirm || isLoading}
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export type DangerZoneProps = {
  children: ReactNode;
  className?: string;
};

function DangerZone({ children, className }: Readonly<DangerZoneProps>) {
  return (
    <div
      className={cn(
        'rounded-lg border border-error/40 bg-error/5 p-4',
        className
      )}
    >
      <div className="flex flex-col gap-5 sm:gap-4">
        <Text weight="semibold" variant="error">
          Danger Zone
        </Text>
        {children}
      </div>
    </div>
  );
}

export { DangerZone, DangerZoneItem };
export default DangerZone;
